import { useState, useEffect, FC } from 'react'
import { useLazyQuery, useMutation } from '@apollo/client'
import { Redirect, useHistory } from 'react-router-dom'
import { useAppDispatch } from 'hooks/store'
import useLoadingDelay from 'hooks/useLoadingDelay'
import { useWindowSize, Size } from 'hooks/useWindowSize'
import { loadingSpinnerPortal } from 'components/atoms/LoadingSpinner'
import { setAuthentication } from 'reducers/authentication/authenticationSlice'
import { NextData } from 'components/features/accountcreation/NewAccountMain'
import NewAccountCreateYourPasswordForm from 'components/organisms/NewAccountCreateYourPasswordForm'
import {
  GET_BY_INVITATION,
  GetByInvitationResult
} from 'share/queries/accountcreation'
import {
  CreatePasswordValidationType,
  ConfirmPasswordValidationType,
  createPasswordValidationTypeInitial,
  confirmPasswordValidationTypeInitial
} from 'share/utils/passwordUtils'
import {
  CREATE_PASSWORD,
  CreatePasswordResponse,
  CreatePasswordResponseData,
  CreatePasswordVariables
} from 'share/mutations/accountcreation'
import {
  loginUser,
  loadTheReduxStoreWithAuthenticationInfoHelper
} from 'share/utils/utils'
import { UserLoginResponse } from 'share/queries/login'
import { AccountCreationError } from 'share/utils/codeVerificationUtils'
import { Button } from 'react-bootstrap'

import './styles.scss'

interface NewAccountCreateYourPasswordProps {
  invitationId: string
  nextStep(data: NextData): void
  setError(error: AccountCreationError): void
}

const NewAccountCreateYourPassword: FC<NewAccountCreateYourPasswordProps> = ({
  invitationId,
  nextStep,
  setError
}) => {
  const dispatch = useAppDispatch()
  const history = useHistory()
  const [invitationHasExpired, setInvitationHasExpired] = useState(false)
  const [alreadyHasAccount, setAlreadyHasAccount] = useState(false)
  const [userId, setUserId] = useState('')
  const [emailAddress, setEmailAddress] = useState('')
  const [verifyPhone, setVerifyPhone] = useState(true)
  const [verifyEmail, setVerifyEmail] = useState(true)
  const [placement, setPlacement] = useState<'right' | 'top'>('top')
  const [createPasswordValidationResult, setCreatePasswordValidationResult] =
    useState<CreatePasswordValidationType>(() =>
      createPasswordValidationTypeInitial()
    )

  const [confirmPasswordValidationResult, setConfirmPasswordValidationResult] =
    useState<ConfirmPasswordValidationType>(() =>
      confirmPasswordValidationTypeInitial()
    )

  const size: Size = useWindowSize()

  const [lookupUserInfo, { loading, data, error }] =
    useLazyQuery<GetByInvitationResult | null>(GET_BY_INVITATION, {
      fetchPolicy: 'network-only'
    })

  const [createPassword, { loading: createPasswordLoading }] =
    useMutation<CreatePasswordResponse, CreatePasswordVariables>(
      CREATE_PASSWORD
    )

  useEffect(() => {
    if (invitationId) {
      lookupUserInfo({ variables: { invitationGuid: invitationId } })
    }
  }, [invitationId])

  useEffect(() => {
    if (data) {
      if (data?.accounts?.byInvitationToken?.userName) {
        setUserId(data?.accounts?.byInvitationToken?.userName)
      }
      if (data?.accounts?.byInvitationToken?.emailAddress) {
        setEmailAddress(data?.accounts?.byInvitationToken?.emailAddress)
      }

      setVerifyPhone(data?.accounts?.byInvitationToken?.verifyPhone ?? 0)

      setVerifyEmail(data?.accounts?.byInvitationToken?.verifyEmail ?? 0)

      if (data?.accounts?.byInvitationToken?.invitationHasExpired) {
        setInvitationHasExpired(true)
      }

      if (data?.accounts?.byInvitationToken?.alreadyHasAccount) {
        setAlreadyHasAccount(true)
      }
    } else {
      console.log('data is null or undefined')
    }
  }, [data])

  const loadings = loading || createPasswordLoading || !emailAddress

  const loadingDelay = useLoadingDelay(loadings)

  useEffect(() => {
    if (size.width && size.width <= 768) {
      setPlacement('top')
    } else {
      setPlacement('right')
    }
  }, [size])

  if (error)
    return (
      <p data-testid='create-account-login-network-error-text'>
        Client Portal is experiencing some difficulties please try again later{' '}
      </p>
    )

  if (invitationHasExpired)
    return (
      <div
        className='ui-expired-account'
        data-testid='create-account-login-expired-account-region'
      >
        <h1 data-testid='create-account-login-expired-account-header-1'>
          Link Expired
        </h1>
        <h2 data-testid='create-account-login-expired-account-header-2'>
          Your link to create your client portal account has expired
        </h2>
        <span data-testid='create-account-login-expired-account-create-instr'>
          To create an account, contact your attorney to request a new{' '}
          invitation. If you have already created your account, please login.
        </span>
        <div className='ui-expired-button'>
          <Button
            variant='primary'
            data-testid='create-account-login-button'
            onClick={() => history.push('/login')}
          >
            Log In
          </Button>
        </div>
      </div>
    )

  if (alreadyHasAccount) {
    return <Redirect to='/login' />
  }

  function loadTheReduxStoreWithAuthenticationInfo(
    input: UserLoginResponse
  ): void {
    const authentication = loadTheReduxStoreWithAuthenticationInfoHelper(input)
    dispatch(setAuthentication(authentication))
  }

  async function loginUserHelper(password: string) {
    try {
      const loginResponse = await loginUser({
        userId: userId,
        password: password,
        fromAccountCreationFlag: true
      })

      if (
        loginResponse.data.authentication.tokenFromUsernameAndPassword.idToken
      ) {
        loadTheReduxStoreWithAuthenticationInfo(loginResponse.data)
      } else {
        console.log('AccountCreationError.GeneralError happened')
        setError(AccountCreationError.GeneralError)
      }
    } catch (error) {
      console.log('loginusererror', error)
      setError(AccountCreationError.GeneralError)
    }
  }

  async function createPasswordHelper(
    createPasswordVariables: CreatePasswordVariables
  ): Promise<CreatePasswordResponseData> {
    const result: CreatePasswordResponseData = {
      success: false,
      mobilePhone: '',
      error: ''
    }

    try {
      const createPasswordResult = await createPassword({
        variables: createPasswordVariables
      })
      if (createPasswordResult.data?.authentication.signUp.success) {
        result.success = true
        result.mobilePhone =
          createPasswordResult.data?.authentication.signUp.mobilePhone

        await loginUserHelper(createPasswordVariables.signup.password)
      } else {
        result.success = false
        result.error = 'Error saving the password. Please try again.'
      }
    } catch (e) {
      result.success = false
      result.error =
        'Your password could not be created at this time. Please try again later.'
    }
    return result
  }

  if (!emailAddress) {
    return <div>loading ...</div>
  }

  return (
    <div className='ui-create-pwd-page'>
      {loadingDelay && loadingSpinnerPortal()}
      <div className='default-size-container mt-4 mt-lg-5'>
        <h1 data-testid='create-account-password-heading'>
          Create your password
        </h1>
        <div
          className='info-description'
          data-testid='create-account-password-description'
        >
          Keep your information safe. We recommend that you memorize it and
          don't write it down.
        </div>
      </div>

      <div className='default-size-container col pr-0 pl-0'>
        <NewAccountCreateYourPasswordForm
          invitationId={invitationId}
          userId={userId}
          emailAddress={emailAddress}
          verifyPhone={verifyPhone}
          verifyEmail={verifyEmail}
          placement={placement}
          createPasswordValidationResult={createPasswordValidationResult}
          setCreatePasswordValidationResult={setCreatePasswordValidationResult}
          confirmPasswordValidationResult={confirmPasswordValidationResult}
          setConfirmPasswordValidationResult={
            setConfirmPasswordValidationResult
          }
          nextStep={nextStep}
          createPasswordHelper={createPasswordHelper}
        />
      </div>
    </div>
  )
}

export default NewAccountCreateYourPassword
